// ВТОРОЙ ИСТОЧНИК ЭКРАНОВ СМАРТ-ПРОЦЕССОВ: своя схема через раздел документов ядра
// (GET /api/docs/list?src=own — own-ветка docs_api.py), с переводом формы туда и обратно.
//
// Экраны /smart/* НЕ переписаны: они зовут тот же useItems с теми же параметрами в форме
// Bitrix REST, а этот модуль решает, куда пойдёт запрос, — ровно так же, как api/own.ts
// решает это для сделок, компаний и контактов. Отличие одно: ручки «crm.item.list из
// своей схемы» у ядра нет, и завести её нельзя без правки общих файлов (белый список
// _DOCS_ПУТИ в web/bff/main.py и монтирование в core_api.py — оба правит только основная
// сессия). Зато у ядра УЖЕ есть двухисточниковый раздел документов /docs/list, и его
// own-ветка отдаёт все четыре перенесённых вида: счета клиенту (31 → invoice out), счета
// на оплату (1160 → invoice in), договоры (153 → contract), АВР (170 → act). Поэтому
// перевод здесь двусторонний: параметры Bitrix → запрос /docs/list, строка документа →
// запись смарт-процесса. Коды полей — ровно те, которыми перенос заполнял свою схему
// (migrate_sys.py, docs_api.ТИПЫ), а не подобранные по смыслу.
//
// Чего в своей схеме НЕТ КАК ДАННЫХ — и что поэтому честно остаётся зеркалу:
//   • «Отчёты по сделкам» (176) и дебиторка (134) документами не переносились — их
//     разделы целиком читают зеркало (и экран смарт-процесса говорит об этом вслух);
//   • названий карточек Bitrix (title) нет — в колонке «Название» прочерк, это не потеря;
//   • стадий Bitrix нет: своя схема живёт состояниями draft/issued/cancelled, и в колонке
//     «Стадия» показана ПОДПИСЬ СОСТОЯНИЯ своей схемы (Черновик/Выпущен/Аннулирован),
//     а фильтр по стадии или воронке уводит запрос в зеркало;
//   • прочих UF-полей типа нет — их колонки пусты; что именно перенесено, названо
//     в ПОЛЯ_ПЕРЕНОСА ниже и в оговорке на экране.
//
// Непереводимый запрос НЕ обрезается молча: он честно объявляется непереводимым и уходит
// в зеркало (тот же принцип и тот же довод, что в own.ts — обрезать фильтр значило бы
// показать полный список под видом отфильтрованного).

import { источник, сделкиМожноИзСвоей } from './istochnik'
import { listItems } from './client'
import { CoreUnsupported, Unauthorized } from './client'
import type { CrmItem, ListParams, ListResult } from './types'

// ─────────────────────────── что перенесено и под какими кодами ───────────────────────────

/** Тип смарт-процесса → вид own-ветки /docs (закрытый список: только перенесённое). */
export const ВИД_OWN: Record<number, string> = {
  31: 'invoices-out',
  1160: 'invoices-in',
  153: 'contracts',
  170: 'acts',
}

/** Почему типа нет в своей схеме — словами, а не молчанием. */
export const НЕ_ПЕРЕНЕСЕНО: Record<number, string> = {
  176: 'отчёты по сделкам (тип 176) в свою схему не переносили',
  134: 'дебиторка (тип 134) перенесена платежами, а не документами — экран считает долг по зеркалу',
}

/**
 * Коды полей Bitrix, которыми перенесённые колонки возвращаются на экран.
 *
 * Это ОБРАТНАЯ сторона переноса: migrate_sys.py читал из этих кодов, когда заполнял
 * invoice/contract/act, поэтому подставить own-значение под тот же код — не догадка,
 * а возврат значения на его место. Поле, которого здесь нет, перенесено НЕ БЫЛО, и
 * рисовать его из own нечем (пример: «Оплачен» ufCrm_1487506483 у счетов — перенос
 * брал дату оплаты из ufCrm_61C5B58222E68, и только её можно честно вернуть).
 */
export const ПОЛЯ_ПЕРЕНОСА: Record<number, {
  номер: string[]
  дата: string[]
  срок: string[]
  оплачен: string[]
  /** Куда класть сумму акта (у 170 она лежит в UF-поле, а не в opportunity). */
  суммаUF?: string
  /** Сумма и валюта в стандартных opportunity/currencyId (счета — да, договоры — нет). */
  деньгиСтандартные: boolean
}> = {
  31: {
    номер: ['accountNumber', 'ufCrm_61C5B582672ED'],
    дата: ['begindate'], срок: ['closedate'], оплачен: ['ufCrm_61C5B58222E68'],
    деньгиСтандартные: true,
  },
  1160: {
    номер: ['ufCrmPropertyNumber'],
    дата: ['ufCrmPropertyDate', 'begindate'],
    срок: ['ufCrmPropertyPlanovayaDataPlatezha'], оплачен: ['ufCrmPropertyDatePay'],
    деньгиСтандартные: true,
  },
  153: {
    номер: ['ufCrm1_1621407223509'],
    дата: ['ufCrm1_1621407241184'], срок: ['ufCrm1_1663581602493'], оплачен: [],
    деньгиСтандартные: false,
  },
  170: {
    номер: ['ufCrm18_1707467889'],
    дата: ['ufCrm18_1708421256'], срок: [], оплачен: [],
    суммаUF: 'ufCrm18_1708421337',
    деньгиСтандартные: false,
  },
}

// ─────────────────────────── перевод запроса ───────────────────────────

/** Итог решения: либо готовая строка запроса к /api/docs/list, либо причина зеркала. */
export type РешениеИсточника =
  | { из: 'own'; параметры: string }
  | { из: 'зеркало'; причина: string }

const зеркало = (причина: string): РешениеИсточника => ({ из: 'зеркало', причина })

const скаляр = (v: unknown): string | null =>
  (typeof v === 'string' || typeof v === 'number') && String(v).trim() !== ''
    ? String(v).trim()
    : null

const ISO_ДЕНЬ = /^\d{4}-\d{2}-\d{2}$/

/** Простые равенства: ключ Bitrix → параметр /docs/list (значения — номера Bitrix). */
const ПРОСТОЙ_ФИЛЬТР: Record<string, string> = {
  companyId: 'company',
  assignedById: 'assigned',
  parentId2: 'deal',
}

/**
 * Перевод параметров списка. Список переводимого ЗАКРЫТЫЙ — как в own.ts.
 *
 * Фильтры по названию (%title), стадии, воронке и произвольным UF-полям непереводимы:
 * названия, стадий Bitrix и прочих UF-полей в своей схеме нет как данных. Диапазон по
 * дате ДОКУМЕНТА переводится: это ровно date_from/date_to раздела /docs.
 *
 * Сортировки. «Изменена/Создана ↓» переводятся в порядок «по дате документа» — это
 * решение ЯДРА, а не наша вольность: created_at/updated_at своей схемы — про строку
 * схемы (ночь переноса), и docs_api сам называет дату документа честной заменой
 * (см. ПОРЯДОК_СПИСКА_OWN и оговорку created_at). Сумма ↓ переводится только у типов,
 * где сумма — стандартный opportunity: у АВР зеркальный opportunity нулевой у всех
 * строк, и «тот же» порядок из own был бы другим порядком.
 */
export function переводСмарт(typeId: number, params: ListParams): РешениеИсточника {
  const вид = ВИД_OWN[typeId]
  if (!вид) {
    return зеркало(НЕ_ПЕРЕНЕСЕНО[typeId] ?? `тип ${typeId} в свою схему не переносили`)
  }
  const поля = ПОЛЯ_ПЕРЕНОСА[typeId]
  const датаДокумента = поля.дата[0]

  const п = new URLSearchParams()
  п.set('src', 'own')
  п.set('type', вид)

  for (const [ключ, значение] of Object.entries(params.filter ?? {})) {
    const простое = ПРОСТОЙ_ФИЛЬТР[ключ]
    if (простое) {
      const v = скаляр(значение)
      if (v === null) return зеркало(`значение фильтра ${ключ} не одиночный номер`)
      п.set(простое, v)
      continue
    }
    if (ключ === `>=${датаДокумента}` || ключ === `<=${датаДокумента}`) {
      const v = скаляр(значение)
      if (v === null || !ISO_ДЕНЬ.test(v)) return зеркало(`${ключ}: значение не дата`)
      п.set(ключ.startsWith('>=') ? 'date_from' : 'date_to', v)
      continue
    }
    return зеркало(`фильтр «${ключ}» своя схема не умеет`)
  }

  const пары = Object.entries(params.order ?? {})
  if (пары.length > 1) return зеркало(`сортировка по ${пары.length} полям сразу`)
  if (пары.length === 1) {
    const [поле, направление] = [пары[0][0], String(пары[0][1]).toUpperCase()]
    const своё = (() => {
      if ((поле === 'updatedTime' || поле === 'createdTime') && направление === 'DESC') return 'date'
      if (поле === датаДокумента && направление === 'DESC') return 'date'
      if (поле === поля.срок[0] && направление === 'ASC') return 'due'
      if (поле === 'opportunity' && направление === 'DESC' && поля.деньгиСтандартные) return 'amount'
      return null
    })()
    if (своё === null) return зеркало(`сортировка «${поле} ${направление}»`)
    п.set('sort', своё)
  }

  if (params.start) п.set('start', String(params.start))
  return { из: 'own', параметры: п.toString() }
}

/**
 * Куда пойдёт запрос списка. Единственное место решения: и транспорт, и оговорка на
 * экране зовут его — иначе подпись «читается своя схема» однажды разошлась бы с тем,
 * куда запрос ушёл на самом деле.
 *
 * Предохранитель зоны — тот же и по той же причине, что у сделок (istochnik.ts):
 * /api/bx сужает списки смарт-процессов зоной роли (ПОЛЕ_СУЖЕНИЯ в web/bff/rights.py),
 * а /api/docs зоной не сужает. Отдать own-ответ учётке с границами значило бы показать
 * ей чужие счета. Убирается это правкой BFF, а не отсюда.
 */
export function откудаСмарт(typeId: number, params: ListParams): РешениеИсточника {
  if (источник() !== 'own') return зеркало('выбран источник mirror')
  if (!сделкиМожноИзСвоей()) {
    return зеркало('выборка учётки сужена зоной, а своя схема зоной пока не сужается')
  }
  return переводСмарт(typeId, params)
}

// ─────────────────────────── перевод ответа ───────────────────────────

/** Строка own-ветки /docs/list — ровно те поля, что кладёт docs_api.список_own. */
export interface ДокОwn {
  id?: string | null
  bitrix_id?: number | null
  doc_state?: string | null
  state_name?: string | null
  doc_number?: string | null
  doc_date?: string | null
  due_date?: string | null
  paid_date?: string | null
  amount?: number | null
  currency?: string | null
  company_bitrix_id?: number | null
  deal_bitrix_id?: number | null
  assigned_bitrix_id?: number | null
}

/** Ответ /docs/list (см. docs_api._выборка): rows вместо items, next числом или null. */
interface СписокДокументов {
  rows: ДокОwn[]
  total: number
  next: number | null
}

/**
 * Строка документа → запись смарт-процесса под кодами полей Bitrix.
 *
 * Пустое остаётся отсутствующим, а не null-ом: экран рисует «—» сам, и отличие
 * «поля нет в ответе» от «поле есть и пустое» здесь не несёт смысла — в своей схеме
 * это одно и то же состояние.
 *
 * stageId — подпись СОСТОЯНИЯ своей схемы (Черновик/Выпущен/Аннулирован), не стадия
 * Bitrix. refs.itemStageName незнакомый код показывает как есть — то есть человек
 * видит слово состояния, а не выдуманное соответствие стадии.
 *
 * createdTime/updatedTime НЕ заполняются: в своей схеме это время строки схемы (ночь
 * переноса), выдавать его за время Bitrix — враньё (то же решение, что у crm_own_api
 * с DATE_MODIFY).
 */
export function записьИзДокумента(typeId: number, р: ДокОwn): CrmItem {
  const поля = ПОЛЯ_ПЕРЕНОСА[typeId]
  const запись: CrmItem = {
    id: р.id ?? String(р.bitrix_id ?? ''),
  }
  if (р.state_name) запись.stageId = р.state_name
  if (р.company_bitrix_id) запись.companyId = р.company_bitrix_id
  if (р.deal_bitrix_id) запись.parentId2 = р.deal_bitrix_id
  if (р.assigned_bitrix_id) запись.assignedById = р.assigned_bitrix_id
  if (поля.деньгиСтандартные && р.amount !== null && р.amount !== undefined) {
    запись.opportunity = р.amount
    if (р.currency) запись.currencyId = р.currency
  }
  if (поля.суммаUF && р.amount !== null && р.amount !== undefined) {
    запись[поля.суммаUF] = String(р.amount)
  }
  const подставить = (коды: string[], значение: string | null | undefined) => {
    if (значение === null || значение === undefined || значение === '') return
    for (const код of коды) запись[код] = значение
  }
  подставить(поля.номер, р.doc_number)
  подставить(поля.дата, р.doc_date)
  подставить(поля.срок, р.due_date)
  подставить(поля.оплачен, р.paid_date)
  return запись
}

// ─────────────────────────── транспорт ───────────────────────────

/**
 * Тот же разбор ответа, что в api/docs.ts (и по той же причине продублирован:
 * общего «api/base.ts» в проекте нет, и вводить его ради двадцати строк значило бы
 * поставить всем разделам общую точку отказа).
 */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 от ядра — неверный фильтр. Пустая таблица читалась бы как «записей нет».
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

/**
 * Список записей смарт-процесса с выбором источника. Форма ответа — та же ListResult,
 * что у зеркала: экраны разницы не видят. stale/scope у own не бывает: своя схема не
 * отстаёт от самой себя, а зоной own-запросы не пускаются вовсе (см. откудаСмарт).
 */
export async function списокЗаписей<T = CrmItem>(
  typeId: number, params: ListParams = {},
): Promise<ListResult<T>> {
  const решение = откудаСмарт(typeId, params)
  if (решение.из === 'own') {
    const с = await запрос<СписокДокументов>(`/api/docs/list?${решение.параметры}`)
    return {
      items: (с.rows ?? []).map((р) => записьИзДокумента(typeId, р)) as T[],
      total: с.total ?? 0,
      next: с.next ?? undefined,
    }
  }
  return listItems<T>(typeId, params)
}

// ─────────────────────────── оговорка для экрана ───────────────────────────

/**
 * Что сказать на экране про источник. null — источник зеркало по выбору человека,
 * говорить нечего (это сегодняшнее привычное состояние раздела).
 *
 * Когда выбран own, молчать нельзя в обе стороны: если читается своя схема — состав
 * колонок другой (и пустая колонка UF-поля должна объясняться, а не выглядеть потерей);
 * если запрос ушёл в зеркало — человек, переключивший источник, должен видеть, что
 * этот раздел или этот фильтр его выбору не подчинился и почему.
 */
export function оговоркаИсточника(typeId: number, params: ListParams): string | null {
  if (источник() !== 'own') return null
  const решение = откудаСмарт(typeId, params)
  if (решение.из === 'own') {
    return 'Источник — своя схема CRM (перенос из Bitrix), не зеркало. Перенесено только '
      + 'заведённое с 01.01.2025 — так решил владелец, поэтому счётчик записей здесь '
      + 'меньше зеркального, и это не потеря. Перенесены номер, даты, сумма, стороны и '
      + 'состояние (Черновик/Выпущен/Аннулирован — оно показано в колонке «Стадия»); '
      + 'остальных полей Bitrix, включая название карточки, в своей схеме нет — их '
      + 'колонки пусты. Времени правки Bitrix тоже нет: колонки «Создана» и «Изменена» '
      + 'пустые, список упорядочен по дате документа. Фильтры и сортировки, которых '
      + 'своя схема не умеет, уводят запрос в зеркало.'
  }
  return `Источник переключён на свою схему, но этот запрос читает зеркало Bitrix: ${решение.причина}.`
}
